// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Application-Level Audit Logging Service
 *
 * HIPAA Compliance: 164.312(b) - Audit Controls
 *
 * ## How This Works With Medplum's Built-in Audit Logging
 *
 * Medplum automatically creates AuditEvent records for ALL FHIR API operations:
 * - Every read, create, update, delete on FHIR resources
 * - Authentication events (login, logout, token refresh)
 * - Failed access attempts and authorization errors
 *
 * These server-side audit logs are immutable, timestamped, and linked to users.
 * They provide the foundation for HIPAA compliance.
 *
 * ## What This Service Adds (Application-Level Events)
 *
 * This service complements Medplum's audit trail by capturing:
 * 1. **UI-level events** - Navigation, page views, button clicks that don't trigger API calls
 * 2. **Session events** - Session timeout, manual logout with reason
 * 3. **Bulk operations** - Data exports with context (what was exported, why)
 * 4. **Business logic** - Practice switching, settings changes with context
 * 5. **Security alerts** - Suspicious activity detected in the UI
 *
 * ## Architecture
 *
 * ```
 * ┌─────────────────────────────────────────────────────────────────┐
 * │                     HIPAA Audit Trail                           │
 * ├─────────────────────────────┬───────────────────────────────────┤
 * │  Medplum (Automatic)        │  CarePoint (This Service)         │
 * ├─────────────────────────────┼───────────────────────────────────┤
 * │ • All FHIR CRUD operations  │ • Session timeout events          │
 * │ • API authentication        │ • Data export with context        │
 * │ • Authorization failures    │ • Settings changes with context   │
 * │ • Resource access logs      │ • Practice switching              │
 * │ • Query execution           │ • UI security alerts              │
 * └─────────────────────────────┴───────────────────────────────────┘
 * ```
 *
 * Both streams of AuditEvent resources are stored together in Medplum,
 * queryable for compliance reporting and security investigations.
 */

import type { MedplumClient } from '@medplum/core';
import type { AuditEvent, Coding, Reference } from '@medplum/fhirtypes';
import { ext } from '../config/tenant';

// DCM (DICOM) Audit Event Types - standardized codes
const AUDIT_EVENT_TYPE_CODES = {
  // Authentication events
  LOGIN: { code: '110114', display: 'User Authentication', system: 'http://dicomstandard.org/dcm' },
  LOGOUT: { code: '110115', display: 'Logout', system: 'http://dicomstandard.org/dcm' },

  // Data access events
  READ: { code: '110106', display: 'Export', system: 'http://dicomstandard.org/dcm' },
  CREATE: { code: '110107', display: 'Import', system: 'http://dicomstandard.org/dcm' },
  UPDATE: { code: '110111', display: 'Import', system: 'http://dicomstandard.org/dcm' },
  DELETE: { code: '110105', display: 'Delete', system: 'http://dicomstandard.org/dcm' },

  // Security events
  SECURITY_ALERT: { code: '110113', display: 'Security Alert', system: 'http://dicomstandard.org/dcm' },
  PERMISSION_DENIED: { code: '110126', display: 'Access Denied', system: 'http://dicomstandard.org/dcm' },

  // Application events
  APPLICATION_START: { code: '110120', display: 'Application Start', system: 'http://dicomstandard.org/dcm' },
  APPLICATION_STOP: { code: '110121', display: 'Application Stop', system: 'http://dicomstandard.org/dcm' },

  // Query events
  QUERY: { code: '110112', display: 'Query', system: 'http://dicomstandard.org/dcm' },
} as const;

// Outcome codes
const AUDIT_OUTCOME = {
  SUCCESS: '0' as const,
  MINOR_FAILURE: '4' as const,
  SERIOUS_FAILURE: '8' as const,
  MAJOR_FAILURE: '12' as const,
};

// Action codes
const AUDIT_ACTION = {
  CREATE: 'C' as const,
  READ: 'R' as const,
  UPDATE: 'U' as const,
  DELETE: 'D' as const,
  EXECUTE: 'E' as const,
};

export type AuditEventType = keyof typeof AUDIT_EVENT_TYPE_CODES;
export type AuditOutcome = (typeof AUDIT_OUTCOME)[keyof typeof AUDIT_OUTCOME];
export type AuditAction = (typeof AUDIT_ACTION)[keyof typeof AUDIT_ACTION];

interface AuditLogEntry {
  /** Type of event */
  type: AuditEventType;
  /** Action performed (C, R, U, D, E) */
  action: AuditAction;
  /** Outcome of the action */
  outcome: AuditOutcome;
  /** Human-readable description */
  description: string;
  /** Resource that was accessed/modified */
  resourceReference?: Reference;
  /** Resource type (e.g., 'Patient', 'Observation') */
  resourceType?: string;
  /** Patient reference (if PHI access) */
  patientReference?: Reference;
  /** Additional details */
  details?: Record<string, string>;
  /** Source IP address (if available) */
  sourceIp?: string;
}

class AuditLogger {
  private medplum: MedplumClient | null = null;
  private organizationId: string | null = null;
  private practitionerId: string | null = null;
  private practitionerName: string | null = null;

  /**
   * Initialize the audit logger with Medplum client and context
   */
  initialize(
    medplum: MedplumClient,
    options?: {
      organizationId?: string;
      practitionerId?: string;
      practitionerName?: string;
    }
  ): void {
    this.medplum = medplum;
    this.organizationId = options?.organizationId || null;
    this.practitionerId = options?.practitionerId || null;
    this.practitionerName = options?.practitionerName || null;
  }

  /**
   * Update the context (e.g., when switching organizations)
   */
  updateContext(options: {
    organizationId?: string;
    practitionerId?: string;
    practitionerName?: string;
  }): void {
    if (options.organizationId !== undefined) {
      this.organizationId = options.organizationId;
    }
    if (options.practitionerId !== undefined) {
      this.practitionerId = options.practitionerId;
    }
    if (options.practitionerName !== undefined) {
      this.practitionerName = options.practitionerName;
    }
  }

  /**
   * Log an audit event
   */
  async log(entry: AuditLogEntry): Promise<AuditEvent | null> {
    if (!this.medplum) {
      console.warn('AuditLogger not initialized - event not logged:', entry.type);
      return null;
    }

    try {
      const typeCode = AUDIT_EVENT_TYPE_CODES[entry.type];

      // Build the AuditEvent resource
      const auditEvent: AuditEvent = {
        resourceType: 'AuditEvent',
        type: {
          system: typeCode.system,
          code: typeCode.code,
          display: typeCode.display,
        },
        subtype: entry.resourceType
          ? [
              {
                system: 'http://hl7.org/fhir/restful-interaction',
                code: entry.action === 'C' ? 'create' : entry.action === 'R' ? 'read' : entry.action === 'U' ? 'update' : entry.action === 'D' ? 'delete' : 'operation',
                display: entry.action === 'C' ? 'create' : entry.action === 'R' ? 'read' : entry.action === 'U' ? 'update' : entry.action === 'D' ? 'delete' : 'operation',
              },
            ]
          : undefined,
        action: entry.action,
        recorded: new Date().toISOString(),
        outcome: entry.outcome,
        outcomeDesc: entry.outcome === '0' ? 'Success' : 'Failure',
        agent: [
          {
            type: {
              coding: [
                {
                  system: 'http://dicomstandard.org/dcm',
                  code: '110153',
                  display: 'Source Role ID',
                },
              ],
            },
            who: this.practitionerId
              ? { reference: `Practitioner/${this.practitionerId}`, display: this.practitionerName || undefined }
              : undefined,
            name: this.practitionerName || 'Unknown User',
            requestor: true,
            network: entry.sourceIp
              ? {
                  address: entry.sourceIp,
                  type: '2', // IP Address
                }
              : undefined,
          },
        ],
        source: {
          site: 'CarePoint Provider App',
          observer: this.organizationId
            ? { reference: `Organization/${this.organizationId}` }
            : { display: 'CarePoint' },
          type: [
            {
              system: 'http://terminology.hl7.org/CodeSystem/security-source-type',
              code: '4',
              display: 'Application Server',
            },
          ],
        },
        entity: this.buildEntities(entry),
      };

      // Add extension for additional details
      if (entry.details) {
        auditEvent.extension = Object.entries(entry.details).map(([key, value]) => ({
          url: ext(`audit-detail-${key}`),
          valueString: value,
        }));
      }

      // Create the audit event
      const result = await this.medplum.createResource(auditEvent);
      return result;
    } catch (error) {
      // Don't throw - audit logging failures shouldn't break the app
      console.error('Failed to create audit event:', error);
      return null;
    }
  }

  private buildEntities(entry: AuditLogEntry): AuditEvent['entity'] {
    const entities: AuditEvent['entity'] = [];

    // Add the resource being accessed
    if (entry.resourceReference) {
      entities.push({
        what: entry.resourceReference,
        type: {
          system: 'http://terminology.hl7.org/CodeSystem/audit-entity-type',
          code: '2',
          display: 'System Object',
        },
        role: {
          system: 'http://terminology.hl7.org/CodeSystem/object-role',
          code: entry.action === 'R' ? '4' : '20',
          display: entry.action === 'R' ? 'Domain Resource' : 'Job',
        },
        description: entry.description,
      });
    }

    // Add patient reference if this is PHI access
    if (entry.patientReference && entry.patientReference.reference !== entry.resourceReference?.reference) {
      entities.push({
        what: entry.patientReference,
        type: {
          system: 'http://terminology.hl7.org/CodeSystem/audit-entity-type',
          code: '1',
          display: 'Person',
        },
        role: {
          system: 'http://terminology.hl7.org/CodeSystem/object-role',
          code: '1',
          display: 'Patient',
        },
      });
    }

    return entities.length > 0 ? entities : undefined;
  }

  // ============================================================================
  // Convenience methods for common audit events
  // ============================================================================

  /**
   * Log a successful login
   */
  async logLogin(userId: string, userName?: string): Promise<void> {
    await this.log({
      type: 'LOGIN',
      action: 'E',
      outcome: '0',
      description: `User ${userName || userId} logged in`,
      details: { userId },
    });
  }

  /**
   * Log a logout
   */
  async logLogout(reason: 'manual' | 'timeout' | 'session_expired' = 'manual'): Promise<void> {
    await this.log({
      type: 'LOGOUT',
      action: 'E',
      outcome: '0',
      description: `User logged out (${reason})`,
      details: { reason },
    });
  }

  /**
   * Log failed login attempt
   */
  async logFailedLogin(userId: string, reason: string): Promise<void> {
    await this.log({
      type: 'LOGIN',
      action: 'E',
      outcome: '4',
      description: `Failed login attempt for ${userId}: ${reason}`,
      details: { userId, reason },
    });
  }

  /**
   * Log PHI access (reading patient data)
   */
  async logPHIAccess(
    resourceType: string,
    resourceId: string,
    patientId?: string,
    description?: string
  ): Promise<void> {
    await this.log({
      type: 'READ',
      action: 'R',
      outcome: '0',
      description: description || `Accessed ${resourceType}/${resourceId}`,
      resourceType,
      resourceReference: { reference: `${resourceType}/${resourceId}` },
      patientReference: patientId ? { reference: `Patient/${patientId}` } : undefined,
    });
  }

  /**
   * Log PHI modification
   */
  async logPHIModification(
    action: 'create' | 'update' | 'delete',
    resourceType: string,
    resourceId: string,
    patientId?: string,
    description?: string
  ): Promise<void> {
    const actionCode = action === 'create' ? 'C' : action === 'update' ? 'U' : 'D';
    const eventType = action === 'create' ? 'CREATE' : action === 'update' ? 'UPDATE' : 'DELETE';

    await this.log({
      type: eventType,
      action: actionCode,
      outcome: '0',
      description: description || `${action} ${resourceType}/${resourceId}`,
      resourceType,
      resourceReference: { reference: `${resourceType}/${resourceId}` },
      patientReference: patientId ? { reference: `Patient/${patientId}` } : undefined,
    });
  }

  /**
   * Log permission denied
   */
  async logPermissionDenied(
    resourceType: string,
    resourceId: string,
    attemptedAction: string
  ): Promise<void> {
    await this.log({
      type: 'PERMISSION_DENIED',
      action: 'R',
      outcome: '8',
      description: `Permission denied: attempted to ${attemptedAction} ${resourceType}/${resourceId}`,
      resourceType,
      resourceReference: { reference: `${resourceType}/${resourceId}` },
      details: { attemptedAction },
    });
  }

  /**
   * Log data export
   */
  async logDataExport(
    exportType: string,
    resourceCount: number,
    patientId?: string
  ): Promise<void> {
    await this.log({
      type: 'READ',
      action: 'R',
      outcome: '0',
      description: `Exported ${resourceCount} ${exportType} records`,
      patientReference: patientId ? { reference: `Patient/${patientId}` } : undefined,
      details: {
        exportType,
        resourceCount: String(resourceCount),
      },
    });
  }

  /**
   * Log settings change
   */
  async logSettingsChange(settingName: string, description?: string): Promise<void> {
    await this.log({
      type: 'UPDATE',
      action: 'U',
      outcome: '0',
      description: description || `Updated setting: ${settingName}`,
      details: { settingName },
    });
  }

  /**
   * Log security alert
   */
  async logSecurityAlert(alertType: string, description: string, severity: 'low' | 'medium' | 'high' = 'medium'): Promise<void> {
    await this.log({
      type: 'SECURITY_ALERT',
      action: 'E',
      outcome: severity === 'high' ? '8' : severity === 'medium' ? '4' : '0',
      description,
      details: { alertType, severity },
    });
  }

  /**
   * Log search/query
   */
  async logSearch(resourceType: string, queryParams: string, resultCount: number): Promise<void> {
    await this.log({
      type: 'QUERY',
      action: 'E',
      outcome: '0',
      description: `Searched ${resourceType} (${resultCount} results)`,
      resourceType,
      details: {
        query: queryParams.substring(0, 500), // Limit query length
        resultCount: String(resultCount),
      },
    });
  }

  /**
   * Log practice/organization switch
   * This is an app-level event not captured by Medplum's automatic auditing
   */
  async logPracticeSwitch(
    fromOrgId: string | undefined,
    toOrgId: string,
    fromOrgName?: string,
    toOrgName?: string
  ): Promise<void> {
    await this.log({
      type: 'UPDATE',
      action: 'E',
      outcome: '0',
      description: `Switched practice from ${fromOrgName || fromOrgId || 'none'} to ${toOrgName || toOrgId}`,
      details: {
        fromOrganization: fromOrgId || 'none',
        toOrganization: toOrgId,
        eventCategory: 'practice_switch',
      },
    });
  }

  /**
   * Log session timeout (HIPAA-specific event)
   * Captures when a user is automatically logged out due to inactivity
   */
  async logSessionTimeout(inactivityMinutes: number): Promise<void> {
    await this.log({
      type: 'LOGOUT',
      action: 'E',
      outcome: '0',
      description: `Session timed out after ${inactivityMinutes} minutes of inactivity (HIPAA auto-logoff)`,
      details: {
        reason: 'timeout',
        inactivityMinutes: String(inactivityMinutes),
        hipaaRequirement: '164.312(a)(2)(iii)',
      },
    });
  }

  /**
   * Log application start (user session begins)
   */
  async logApplicationStart(): Promise<void> {
    await this.log({
      type: 'APPLICATION_START',
      action: 'E',
      outcome: '0',
      description: 'CarePoint Provider App session started',
      details: {
        userAgent: typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent.substring(0, 200),
      },
    });
  }
}

// Export singleton instance
export const auditLogger = new AuditLogger();

// Export types for use elsewhere
export type { AuditLogEntry };
